// Adapter: CallTransport over livekit-client. LiveKit does the WebRTC signaling
// (join, SDP, trickle ICE, reconnect) over its own WebSocket (docs/07).
import {
  ConnectionState,
  Room,
  RoomEvent,
  Track,
  type RemoteParticipant,
  type RemoteTrack,
} from "livekit-client";
import type { SessionDescriptor } from "@stc/proto";
import type { AudioLevels, CallTransport, ConnectOptions, TransportState } from "./call-transport";

const UI_TOPIC = "ui"; // UI control channel v1

const STATE: Record<ConnectionState, TransportState> = {
  [ConnectionState.Disconnected]: "disconnected",
  [ConnectionState.Connecting]: "connecting",
  [ConnectionState.Connected]: "connected",
  [ConnectionState.Reconnecting]: "reconnecting",
  [ConnectionState.SignalReconnecting]: "reconnecting",
};

export class LiveKitTransport implements CallTransport {
  private readonly room = new Room({ adaptiveStream: false, dynacast: false });
  private readonly audioEls = new Map<string, HTMLMediaElement>();
  /** Measures what this page actually receives, not what the server reports. */
  private audioCtx: AudioContext | null = null;
  private readonly analysers = new Map<string, AnalyserNode>();

  constructor() {
    // Remote audio plays unconditionally. Playback must never depend on whether the UI
    // registered a listener (that was the P0 "connected but silent" bug).
    this.room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
      if (track.kind !== Track.Kind.Audio || !track.sid) return;
      const el = track.attach();
      el.setAttribute("playsinline", "");
      el.autoplay = true;
      document.body.appendChild(el);
      this.audioEls.set(track.sid, el);
      this.analyse(track.sid, track.mediaStreamTrack);
    });
    this.room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
      if (!track.sid) return;
      track.detach().forEach((el) => el.remove());
      this.audioEls.delete(track.sid);
      this.analysers.get(track.sid)?.disconnect();
      this.analysers.delete(track.sid);
    });
  }

  async unlockAudio(): Promise<void> {
    // Both are created inside the tap so iOS Safari allows them.
    this.audioCtx ??= new AudioContext();
    await Promise.all([this.room.startAudio(), this.audioCtx.resume()]);
  }

  private analyse(sid: string, track: MediaStreamTrack): void {
    if (!this.audioCtx) return;
    const analyser = this.audioCtx.createAnalyser();
    analyser.fftSize = 512;
    this.audioCtx.createMediaStreamSource(new MediaStream([track])).connect(analyser);
    this.analysers.set(sid, analyser);
  }

  private static rms(analyser: AnalyserNode): number {
    const buf = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(buf);
    let sum = 0;
    for (const v of buf) sum += v * v;
    return Math.min(1, Math.sqrt(sum / buf.length) * 4); // scaled so normal speech reads mid-bar
  }

  async connect(descriptor: SessionDescriptor, options: ConnectOptions = {}): Promise<void> {
    if (descriptor.transport !== "livekit") throw new Error(`unsupported transport ${descriptor.transport}`);
    await this.room.connect(descriptor.url, descriptor.token, {
      autoSubscribe: true,
      ...(options.relayOnly ? { rtcConfig: { iceTransportPolicy: "relay" } } : {}),
    });
    // If the gesture's unlock did not stick (some iOS versions), try again now.
    if (!this.room.canPlaybackAudio) await this.room.startAudio().catch(() => {});
  }

  async enableMic(): Promise<void> {
    await this.room.localParticipant.setMicrophoneEnabled(true, {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    });
  }

  async send(payload: Uint8Array<ArrayBuffer>): Promise<void> {
    await this.room.localParticipant.publishData(payload, { reliable: true, topic: UI_TOPIC });
  }

  onData(handler: (payload: Uint8Array) => void): () => void {
    const listener = (payload: Uint8Array, _p: unknown, _k: unknown, topic?: string) => {
      if (topic === UI_TOPIC) handler(payload);
    };
    this.room.on(RoomEvent.DataReceived, listener);
    return () => this.room.off(RoomEvent.DataReceived, listener);
  }

  onState(handler: (state: TransportState) => void): () => void {
    const listener = (state: ConnectionState) => handler(STATE[state]);
    this.room.on(RoomEvent.ConnectionStateChanged, listener);
    return () => this.room.off(RoomEvent.ConnectionStateChanged, listener);
  }

  onRemoteAudio(handler: (event: { participant: string; playing: boolean }) => void): () => void {
    const on = (track: RemoteTrack, _pub: unknown, p: RemoteParticipant) => {
      if (track.kind === Track.Kind.Audio) handler({ participant: p.identity, playing: true });
    };
    const off = (track: RemoteTrack, _pub: unknown, p: RemoteParticipant) => {
      if (track.kind === Track.Kind.Audio) handler({ participant: p.identity, playing: false });
    };
    this.room.on(RoomEvent.TrackSubscribed, on);
    this.room.on(RoomEvent.TrackUnsubscribed, off);
    return () => {
      this.room.off(RoomEvent.TrackSubscribed, on);
      this.room.off(RoomEvent.TrackUnsubscribed, off);
    };
  }

  canPlayAudio(): boolean {
    return this.room.canPlaybackAudio;
  }

  onAudioPlayback(handler: (allowed: boolean) => void): () => void {
    const listener = (allowed: boolean) => handler(allowed);
    this.room.on(RoomEvent.AudioPlaybackStatusChanged, listener);
    return () => this.room.off(RoomEvent.AudioPlaybackStatusChanged, listener);
  }

  levels(): AudioLevels {
    let remote = 0;
    for (const a of this.analysers.values()) remote = Math.max(remote, LiveKitTransport.rms(a));
    return { local: this.room.localParticipant.audioLevel, remote };
  }

  async close(): Promise<void> {
    await this.room.disconnect();
    this.audioEls.forEach((el) => el.remove());
    this.audioEls.clear();
    this.analysers.clear();
    await this.audioCtx?.close();
    this.audioCtx = null;
  }
}
